import { z } from 'zod';

/**
 * Avatar choice (spec §6.8): one of the premade characters from LimeZu's "Modern Interiors" pack
 * (`tile_assets/04_Characters`, assets-src/LICENSES.md). Users can never upload sprites. The
 * earlier custom paper-doll look is gone (ADR-0033); a stored paper-doll look is read as a
 * premade character (see {@link parseAppearance}).
 */

/** The 20 characters of `Character_Generator/0_Premade_Characters`, in the pack's order. */
const PREMADE_IDS = [
  'premade_01',
  'premade_02',
  'premade_03',
  'premade_04',
  'premade_05',
  'premade_06',
  'premade_07',
  'premade_08',
  'premade_09',
  'premade_10',
  'premade_11',
  'premade_12',
  'premade_13',
  'premade_14',
  'premade_15',
  'premade_16',
  'premade_17',
  'premade_18',
  'premade_19',
  'premade_20',
] as const;

/** The named adults of `Old/Single_Characters_Legacy` that have full idle and walk sheets. */
const NAMED_IDS = [
  'adam',
  'alex',
  'amelia',
  'ash',
  'bob',
  'bruce',
  'conference_man',
  'conference_woman',
  'dan',
  'edward',
  'lucy',
  'molly',
  'pier',
  'rob',
  'roki',
  'samuel',
] as const;

export const CHARACTER_IDS = [...PREMADE_IDS, ...NAMED_IDS] as const;
export type CharacterId = (typeof CHARACTER_IDS)[number];

export function isCharacterId(value: unknown): value is CharacterId {
  return typeof value === 'string' && (CHARACTER_IDS as readonly string[]).includes(value);
}

/** Number of a premade character (`premade_07` → 7), or `undefined` for a named one. */
export function premadeNumber(id: CharacterId): number | undefined {
  return id.startsWith('premade_') ? Number(id.slice('premade_'.length)) : undefined;
}

export interface Appearance {
  readonly character: CharacterId;
}

export const DEFAULT_APPEARANCE: Appearance = { character: 'premade_01' };

/** The server validates every appearance change with this (spec §6.8). */
export const appearanceSchema = z.object({ character: z.enum(CHARACTER_IDS) }).strict();

/** The slots of the old paper-doll look, only used to recognise stored ones. */
const PAPER_DOLL_SLOTS = ['skin', 'hairStyle', 'hairColor', 'outfit', 'accessory'] as const;

/**
 * The premade character that stands in for an old paper-doll look: picked from the look itself,
 * so the same look always becomes the same character and different looks spread across them.
 */
export function fallbackCharacter(seed: string): CharacterId {
  let hash = 0;
  for (const char of seed) hash = (Math.imul(hash, 31) + char.charCodeAt(0)) >>> 0;
  return PREMADE_IDS[hash % PREMADE_IDS.length] ?? DEFAULT_APPEARANCE.character;
}

/** Identifier of an appearance, the character id. It is what the room syncs for each player. */
export function appearanceKey(appearance: Appearance): string {
  return appearance.character;
}

/**
 * Inverse of {@link appearanceKey}. An old paper-doll key (`skin.hairStyle.hairColor.outfit.
 * accessory`, optionally followed by a character) becomes its character, or the fallback one.
 * Returns `null` for anything else.
 */
export function parseAppearanceKey(key: string): Appearance | null {
  if (isCharacterId(key)) return { character: key };
  const parts = key.split('.');
  if (parts.length === PAPER_DOLL_SLOTS.length) return { character: fallbackCharacter(key) };
  const last = parts.at(-1);
  if (parts.length === PAPER_DOLL_SLOTS.length + 1 && isCharacterId(last))
    return { character: last };
  return null;
}

/**
 * Reads a stored appearance (the users table's JSON column), or `null` if none was chosen yet
 * (`{}`). A stored paper-doll look keeps the premade character it had, else gets the fallback.
 */
export function parseAppearance(value: unknown): Appearance | null {
  if (typeof value !== 'object' || value === null) return null;
  const stored = value as Record<string, unknown>;
  if (isCharacterId(stored.character)) return { character: stored.character };
  if (!PAPER_DOLL_SLOTS.some((slot) => typeof stored[slot] === 'string')) return null;
  return {
    character: fallbackCharacter(PAPER_DOLL_SLOTS.map((slot) => String(stored[slot])).join('.')),
  };
}
